import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Search } from 'lucide-react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { ONBOARDING_STATUSES } from '../constants';
import { formatStatusText } from '../utils/statusUtils';
import Pagination from '../components/Pagination';
import Dropdown from '../components/Dropdown';
import StatCard from '../components/shared/stat-card';
import CTable from '../components/shared/c-table';
import usePersistedState from '../hooks/usePersistedState';

const LIMIT = 20;

const OnboardingRequests = () => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({
    pending_review: 0,
    in_review: 0,
    approved: 0,
    rejected: 0,
  });
  const [total, setTotal] = useState(0);
  const [page, setPage] = usePersistedState('onboarding.page', 1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = usePersistedState(
    'onboarding.status',
    ''
  );
  const [search, setSearch] = usePersistedState('onboarding.search', '');
  const [searchInput, setSearchInput] = useState(search);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/api/onboarding/stats');
      setStats(res.data.data);
    } catch (err) {
      // stats are non-critical
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const res = await api.get('/api/onboarding', { params });
      setRequests(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load onboarding requests');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const totalPages = Math.ceil(total / LIMIT);

  const statCards = [
    {
      label: 'Pending Review',
      value: stats.pending_review,
      color: 'border-warning-500',
      textColor: 'text-warning-600',
    },
    {
      label: 'In Review',
      value: stats.in_review,
      color: 'border-blue-500',
      textColor: 'text-blue-600',
    },
    {
      label: 'Approved',
      value: stats.approved,
      color: 'border-success-500',
      textColor: 'text-success-600',
    },
    {
      label: 'Rejected',
      value: stats.rejected,
      color: 'border-danger-500',
      textColor: 'text-danger-600',
    },
  ];

  const getOnboardingBadgeClass = (status) => {
    const map = {
      pending_review: 'bg-warning-100 text-warning-800',
      in_review: 'bg-blue-100 text-blue-800',
      approved: 'bg-success-100 text-success-800',
      rejected: 'bg-danger-100 text-danger-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      cellClassName: 'font-medium',
      render: (req) => `${req.first_name} ${req.last_name}`,
    },
    {
      key: 'email',
      header: 'Email',
      cellClassName: 'text-gray-600',
      render: (req) => req.email,
    },
    {
      key: 'cnic',
      header: 'CNIC',
      cellClassName: 'text-gray-500',
      render: (req) => req.cnic || '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (req) => (
        <span
          data-testid="onboarding-requests-cell-status"
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOnboardingBadgeClass(req.status)}`}
        >
          {formatStatusText(req.status)}
        </span>
      ),
    },
    {
      key: 'submitted',
      header: 'Submitted',
      cellClassName: 'text-gray-500',
      render: (req) =>
        req.created_at ? new Date(req.created_at).toLocaleDateString() : '-',
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (req) => (
        <Link
          to={`/onboarding/requests/${req.id}`}
          data-testid="onboarding-requests-review"
          className="text-primary-600 hover:text-primary-800 font-medium"
        >
          View
        </Link>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ClipboardList className="h-7 w-7 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">
            Onboarding Requests
          </h1>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <StatCard
            key={card.label}
            borderColor={card.color}
            className="p-4 shadow-sm"
          >
            <p className="text-xs text-gray-500">{card.label}</p>
            <p className={`text-2xl font-bold ${card.textColor}`}>
              {card.value}
            </p>
          </StatCard>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={handleSearch} className="flex flex-1 gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
            >
              Search
            </button>
          </form>
          <Dropdown
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            {ONBOARDING_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Dropdown>
        </div>
      </div>

      {/* Table */}
      <CTable
        columns={columns}
        data={requests}
        loading={loading}
        rowKey="id"
        emptyMessage="No onboarding requests found."
        getRowProps={(req) => ({
          'data-testid': `onboarding-requests-row-${req.id}`,
        })}
      />

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
};

export default OnboardingRequests;
